import React, { useEffect, useState } from 'react'
import './style.less'
import { Modal, Row } from 'antd'
import { IDateRange, menuModal } from '../../lib/interfaces'
import {
    convertDateToDKTime,
    formatText,
    isInsidePeriod,
    layoutDictKeyFormat,
    userNotesReceiver,
} from '../../lib/dateFunctions'
import { addDays, isSameDay, toDate } from 'date-fns'
import { Camera } from '../Camera/index'
import { UserNoteRow } from './UserNoteRow'
import { httpPost } from '../../services/http'
import { ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { colorConverter } from '../../lib/userNoteColors'
import { APIFunctions, getUserTypeFunctions } from '../../lib/apiFunctions'
import { ComplianceReportsTypes } from '../../lib/enums'
import { WhereAbouts } from './WhereAbouts'
import { Button, CircularProgress, IconButton, Popover, Theme, Typography } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import InfoIcon from '@mui/icons-material/Info'
import { isMobile } from 'react-device-detect'
import { useLocation } from '@reach/router'
import DateSpecification from './DateSpecification'
import { hasFeatureFlag } from '../../hooks/useAuth'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        header: {
            width: '100%',
            marginBottom: 10,
            justifyContent: 'center',
        },
        headerText: {
            margin: 'auto',
            marginLeft: 10,
            marginRight: 10,
        },
        typography: {
            padding: theme.spacing(2),
        },
        infoContainer: {
            marginRight: '20px',
        },
    })
)

const useKeyPress = function (
    targetKey,
    keyDownHandler,
    keyUpHandler,
    changeNoteContent,
    whereAboutsDropdownOpen
) {
    function downHandler({ key }) {
        if (!whereAboutsDropdownOpen && !changeNoteContent && keyDownHandler && key === targetKey) {
            keyDownHandler()
        }
    }

    const upHandler = ({ key }) => {
        if (!changeNoteContent && keyUpHandler && key === targetKey) {
            keyUpHandler()
        }
    }

    React.useEffect(() => {
        if (typeof window !== 'undefined') {
            window.addEventListener('keydown', downHandler)
            window.addEventListener('keyup', upHandler)

            return () => {
                window.removeEventListener('keydown', downHandler)
                window.removeEventListener('keyup', upHandler)
            }
        }
    })
}

export function MenuModal({
    dateClicked,
    setCurrentDate,
    visible,
    setVisible,
    title,
    startPeriod,
    dateRanges,
    deletePeriod,
    docDates,
    setDocDates,
    rosterDates,
    userNotes,
    setUserNotes,
    userLocationDates,
    setUserLocationDates,
    updateData,
    session,
    setIsBackgroundLoading,
}: menuModal) {
    const [period, setPeriod] = useState<IDateRange | false>(false)
    const [userNote, setUserNote] = useState<userNotesReceiver | false>(false)
    const [rosterCategory, setRosterCategory] = useState<string | false>(false)
    const [documentVisible, setDocumentVisible] = useState<boolean>(false)
    // const [noteVisible, setNoteVisible] = useState<boolean>(false)
    const [changeNoteContent, setChangeNoteContent] = useState<boolean>(false)
    const [addNoteLoading, setAddNoteLoading] = useState<boolean>(false)
    const [apiFunctions, setAPIFunctions] = useState<APIFunctions | null>(null)
    const [whereAboutsDropdownOpen, setWhereAboutsDropdownOpen] = useState<boolean>(false)

    const [anchorInfoVisible, setAnchorInfoVisible] = useState<HTMLButtonElement | null>(null)
    const { search } = useLocation()

    const infoVisible = Boolean(anchorInfoVisible)
    const infoVisibleId = infoVisible ? 'simple-popover' : undefined
    const classes = useStyles()

    useEffect(() => {
        setAPIFunctions(getUserTypeFunctions(ComplianceReportsTypes.LLUser))
    }, [])

    function openDocumentIfRequested(documentationId?: string) {
        const parameters = new URLSearchParams(search)
        const documentId = parameters.get('view_document_id')
        if (documentationId ?? documentId) {
            setDocumentVisible(true)
        }
    }

    useEffect(() => {
        openDocumentIfRequested()
    }, [search])

    const startPeriodClick = () => {
        startPeriod(dateClicked)
        setVisible(false)
    }

    const deletePeriodClick = () => {
        setVisible(false)
        deletePeriod(period)
    }

    useEffect(() => {
        setChangeNoteContent(false)
    }, [dateClicked])

    useEffect(() => {
        setPeriod(isInsidePeriod(dateRanges, convertDateToDKTime(dateClicked)))
    }, [dateClicked, dateRanges])

    useEffect(() => {
        setUserNote(
            userNotes.find((n) =>
                isSameDay(convertDateToDKTime(n.target_date), convertDateToDKTime(dateClicked))
            ) || false
        )
    }, [dateClicked, userNotes])

    useEffect(() => {
        setRosterCategory(
            rosterDates.find((n) =>
                isSameDay(convertDateToDKTime(n.roster_date), convertDateToDKTime(dateClicked))
            )?.categories || false
        )
    }, [dateClicked, rosterDates])

    const openCameraModal = () => {
        setDocumentVisible(true)
    }

    const addNote = async () => {
        setAddNoteLoading(true)
        await httpPost(`/user-notes/${formatText(dateClicked, layoutDictKeyFormat)}`, {
            content: '',
            color: 'purple',
            include_report: true,
            include_graph: true,
        })
        // setUserNotes(mapUserNotes((await httpGet<userNotesReceiver[]>('/user-notes/')).data));
        const newUserNotes: userNotesReceiver[] = [
            ...userNotes,
            {
                target_date: new Date(dateClicked),
                note_color: colorConverter['purple'],
                note_content: '',
                include_report: true,
                include_graph: true,
                created_on: null,
                modified_on: null,
            },
        ]
        setUserNotes(newUserNotes)
        setAddNoteLoading(false)
    }

    const closeModal = () => {
        setVisible(false)
        setDocumentVisible(false)
        window.history.replaceState(null, null as any, '?')
        setPeriod(false)
    }

    function changeDateRight() {
        setCurrentDate(addDays(dateClicked, 1))
    }

    function changeDateleft() {
        setCurrentDate(addDays(dateClicked, -1))
    }

    useKeyPress('ArrowLeft', changeDateleft, null, changeNoteContent, whereAboutsDropdownOpen)
    useKeyPress('ArrowRight', changeDateRight, null, changeNoteContent, whereAboutsDropdownOpen)

    async function reportChange(action: any) {
        switch (action.action) {
            case 'exclude':
            case 'include':
                const sumData = await apiFunctions.getDocDateSum(
                    session.user.user_id,
                    action.targetDate
                )
                if (
                    sumData.count > 0 &&
                    !docDates.find(
                        (dd) =>
                            dd.documentation_date === action.targetDate &&
                            dd.date_is_included_in_report
                    )
                ) {
                    setDocDates(
                        docDates.map((d) => {
                            if (
                                isSameDay(
                                    new Date(d.documentation_date),
                                    toDate(new Date(action.targetDate))
                                )
                            ) {
                                return {
                                    documentation_date: new Date(d.documentation_date),
                                    date_is_included_in_report: true,
                                }
                            }
                            return d
                        })
                    )
                } else if (sumData.count <= 0) {
                    setDocDates(
                        docDates.map((d) => {
                            if (
                                isSameDay(
                                    new Date(d.documentation_date),
                                    toDate(new Date(action.targetDate))
                                )
                            ) {
                                return {
                                    documentation_date: new Date(d.documentation_date),
                                    date_is_included_in_report: false,
                                }
                            }
                            return d
                        })
                    )
                }
                break
            case 'openDocument':
                openDocumentIfRequested(action.documentationId)
                break
        }
    }

    return (
        <Modal visible={visible} onCancel={closeModal} onOk={closeModal} footer={[]}>
            {!period && (
                <Row className={classes.header}>
                    <IconButton
                        className={classes.infoContainer}
                        onClick={(e) => {
                            setAnchorInfoVisible(e.currentTarget)
                        }}
                        edge="start"
                        aria-label="info"
                        size="large"
                    >
                        <InfoIcon />
                    </IconButton>
                    <IconButton edge="start" aria-label="left" size="large">
                        <ArrowLeftOutlined
                            style={{
                                alignSelf: 'center',
                                userSelect: 'none',
                                fontSize: 20,
                            }}
                            onClick={changeDateleft}
                        />
                    </IconButton>
                    <h3 className={classes.headerText}>{title && title}</h3>
                    <IconButton edge="end" aria-label="right" size="large">
                        <ArrowRightOutlined
                            onClick={changeDateRight}
                            style={{
                                alignSelf: 'center',
                                userSelect: 'none',
                                fontSize: 20,
                            }}
                        />
                    </IconButton>
                    <div style={{ width: 68 }} />
                </Row>
            )}
            {period && (
                <Row className={classes.header}>
                    <IconButton
                        className={classes.infoContainer}
                        onClick={(e) => {
                            setAnchorInfoVisible(e.currentTarget)
                        }}
                        edge="start"
                        aria-label="info"
                        size="large"
                    >
                        <InfoIcon />
                    </IconButton>
                    <IconButton edge="start" aria-label="left" size="large">
                        <ArrowLeftOutlined
                            style={{
                                alignSelf: 'center',
                                userSelect: 'none',
                                fontSize: 20,
                            }}
                            onClick={changeDateleft}
                        />
                    </IconButton>
                    {!isMobile && (
                        <h3 className={classes.headerText}>
                            {title} in:{' '}
                            {formatText(toDate(period.start), 'dd. MMM') +
                                ' - ' +
                                formatText(toDate(period.end), 'dd. MMM')}
                        </h3>
                    )}
                    <IconButton edge="end" aria-label="right" size="large">
                        <ArrowRightOutlined
                            onClick={changeDateRight}
                            style={{
                                alignSelf: 'center',
                                userSelect: 'none',
                                fontSize: 20,
                            }}
                        />
                    </IconButton>
                    <div style={{ width: 68 }} />
                    {isMobile && (
                        <h3 className={classes.headerText}>
                            {title} in:{' '}
                            {formatText(toDate(period.start), 'dd. MMM') +
                                ' - ' +
                                formatText(toDate(period.end), 'dd. MMM')}
                        </h3>
                    )}
                </Row>
            )}
            {rosterCategory && (
                <Row className="menuRow">
                    <p>Roster duty: {rosterCategory}</p>
                </Row>
            )}
            {!period && (
                <Row className="menuRow">
                    <Button
                        className="autotest-add-dk-vacation-button"
                        id="add-vacation"
                        onClick={startPeriodClick}
                        variant="contained"
                        color="primary"
                    >
                        + Add DK-vacation
                    </Button>
                </Row>
            )}
            {period && (
                <Row className="menuRow">
                    <Button
                        className="autotest-delete-dk-vacation-button"
                        id="delete-vacation"
                        onClick={deletePeriodClick}
                        variant="contained"
                        color="secondary"
                    >
                        Delete DK-vacation
                    </Button>
                </Row>
            )}
            <Row className="menuRow">
                <Button
                    className="autotest-upload-button"
                    onClick={openCameraModal}
                    color="info"
                    variant="contained"
                >
                    View/upload documentation
                </Button>
            </Row>
            {hasFeatureFlag(session, 'date_spec') && (
                <Row className="menuRow">
                    <DateSpecification targetDate={dateClicked} />
                </Row>
            )}
            {!userNote && (
                <Row className="menuRow">
                    <Button
                        className="autotest-add-note-button"
                        onClick={addNote}
                        variant="contained"
                        color="info"
                        endIcon={addNoteLoading ? <CircularProgress color="inherit" /> : null}
                    >
                        + Add note
                    </Button>
                </Row>
            )}
            {userNote && (
                <Row className="menuRow">
                    <UserNoteRow
                        userNote={userNote}
                        setUserNotes={setUserNotes}
                        userNotes={userNotes}
                        updateData={updateData}
                        changeContent={changeNoteContent}
                        setChangeContent={setChangeNoteContent}
                        menuModalVisible={visible}
                    />
                </Row>
            )}
            <Row className="menuRow">
                <WhereAbouts
                    dateClicked={dateClicked}
                    apiFunctions={apiFunctions}
                    userId={session?.user?.user_id}
                    dropdownOpen={whereAboutsDropdownOpen}
                    setDropdownOpen={setWhereAboutsDropdownOpen}
                    setIsBackgroundLoading={setIsBackgroundLoading}
                    userLocationDates={userLocationDates}
                    setUserLocationDates={setUserLocationDates}
                    reportChange={reportChange}
                />
            </Row>
            <Camera
                apiFunctions={getUserTypeFunctions(ComplianceReportsTypes.LLUser)}
                userId={session?.user?.user_id}
                dateClicked={dateClicked}
                visible={documentVisible}
                setVisible={setDocumentVisible}
                docDates={docDates}
                setDocDates={setDocDates}
                title={
                    period ? (
                        <h3>
                            {title} in:{' '}
                            {formatText(toDate(period.start), 'dd. MMM') +
                                ' - ' +
                                formatText(toDate(period.end), 'dd. MMM')}
                        </h3>
                    ) : (
                        <h3>{title && title}</h3>
                    )
                }
                reportChange={reportChange}
            />
            <Popover
                id={infoVisibleId}
                open={infoVisible}
                anchorEl={anchorInfoVisible}
                onClose={() => setAnchorInfoVisible(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Typography className={classes.typography}>
                    Fra denne menu styrer du det data der skal indgå for{' '}
                    {formatText(dateClicked, layoutDictKeyFormat)}.
                    <br />
                    Indsæt DK-dage,
                    <br />
                    overfør dokumentation,
                    <br />
                    tilføj note og/eller
                    <br />
                    angiv de steder du har opholdt dig.
                </Typography>
            </Popover>
        </Modal>
    )
}
